/**
 * Experience snapshot — props canónicos para PostHog + hub admin
 * `/admin/visitor-experience`.
 *
 * Cada evento DualCTA (y super-properties de sesión) debe llevar este contrato
 * para que el operador vea origen · template · componente · variante · precio · mux.
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { resolveAdminExperienceOwnership } from "@/lib/adminExperienceOwnership";

export type ExperienceVariantSource =
  | "ab_slot"
  | "country_template"
  | "env_baseline"
  | "admin_premium_invite"
  | "mux_api"
  | "unknown";

export interface ExperienceSnapshotInput {
  /** Shell de página (p. ej. DualCTAVideoLanding). */
  template: string;
  /** Componente UI principal (p. ej. DualCTAVideoLanding / MuxVideoPlayerWithCTA). */
  component: string;
  productSurface: string;
  workerCountry?: string;
  effectiveCountry?: string;
  /** Qué capa de config ganó el video/precio. */
  variantSource: ExperienceVariantSource;
  slotKey?: string;
  slotEnabled?: boolean;
  variantLabel?: string;
  variantIndex?: number;
  muxPlaybackId?: string | null;
  playbackIdSource?: string;
  vipStripePriceId?: string | null;
  vipPriceOfferLine?: string | null;
  vipDisplayFullUsd?: number | null;
  vipStripePriceSource?: string | null;
  configVersion?: string | null;
  /** Extra props ya resueltas (utm, etc.). */
  extra?: Record<string, unknown>;
}

/**
 * Flat props for posthog.capture / register.
 * Only string | number | boolean | null (PostHog-safe).
 */
export function buildExperienceSnapshot(
  input: ExperienceSnapshotInput,
): Record<string, string | number | boolean | null> {
  const ts =
    typeof window !== "undefined"
      ? detectTrafficSource()
      : { channel: "", source: "", medium: "", inAppBrowser: false, raw: {} as { referrer_domain?: string } };

  const out: Record<string, string | number | boolean | null> = {
    site: BRAND_SOURCE_KEY || "delfuocampo",
    source: BRAND_SOURCE_KEY || "delfuocampo",
    experience_template: input.template,
    experience_component: input.component,
    product_surface: input.productSurface,
    experience_variant_source: input.variantSource,
    // Legacy keys used by existing insights
    template: input.template,
    template_type: input.template,
  };

  if (input.workerCountry) {
    out.worker_country = input.workerCountry;
  }
  if (input.effectiveCountry) {
    out.effective_country = input.effectiveCountry;
    out.checkout_country_effective = input.effectiveCountry;
  }

  if (input.slotKey) out.video_ab_slot_key = input.slotKey;
  if (input.slotEnabled !== undefined) out.video_ab_slot_enabled = input.slotEnabled;

  if (input.variantSource === "ab_slot" && (input.variantLabel || input.variantIndex !== undefined)) {
    out.video_ab_variant_label = input.variantLabel || `variant_${input.variantIndex ?? 0}`;
    if (input.variantIndex !== undefined && input.variantIndex >= 0) {
      out.video_ab_variant_index = input.variantIndex;
    }
  } else if (input.variantSource === "env_baseline" || input.variantSource === "country_template") {
    out.video_ab_variant_label = "baseline";
    out.video_ab_variant_index = -1;
  }

  const mux = input.muxPlaybackId?.trim();
  if (mux) {
    out.mux_playback_id = mux;
    out.playback_id = mux;
  }
  if (input.playbackIdSource) out.playback_id_source = input.playbackIdSource;

  const priceId = input.vipStripePriceId?.trim();
  if (priceId) out.vip_stripe_price_id = priceId;
  if (input.vipPriceOfferLine) out.vip_price_offer_line = input.vipPriceOfferLine;
  if (input.vipDisplayFullUsd != null && Number.isFinite(input.vipDisplayFullUsd)) {
    out.vip_display_full_usd = input.vipDisplayFullUsd;
  }
  if (input.vipStripePriceSource) out.vip_stripe_price_source = input.vipStripePriceSource;

  if (input.configVersion) out.config_version = input.configVersion;

  // Traffic
  if (ts.channel) out.traffic_channel = ts.channel;
  if (ts.source) out.utm_source = ts.source;
  if (ts.medium) out.utm_medium = ts.medium;
  if (ts.inAppBrowser) out.in_app_browser = true;
  if (ts.raw?.referrer_domain) out.referrer_host = ts.raw.referrer_domain;

  if (input.extra) {
    for (const [k, v] of Object.entries(input.extra)) {
      if (v === undefined) continue;
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean" || v === null) {
        out[k] = v;
      }
    }
  }

  return out;
}

/** Deep-link helper used by admin hub (client-side mirrors server). */
export function adminEditPathForExperience(opts: {
  variantSource?: string | null;
  country?: string | null;
  template?: string | null;
  city?: string | null;
}): { path: string; label: string; hint?: string; summary?: string } {
  const o = resolveAdminExperienceOwnership({
    country: opts.country,
    template: opts.template,
    variantSource: opts.variantSource,
    city: opts.city,
  });
  return {
    path: o.primary.path,
    label: o.primary.label,
    hint: o.primary.hint,
    summary: o.summary,
  };
}
